import Vue from 'vue';
import store from '~/store.js';
import i18n from '~/i18n/index.js';
import { getToncenterActions } from '~/api/toncenterV3.js';

// Pages are large and slow on purpose: the export runs on the visitor's
// device, and a pause between requests keeps it under the toncenter limits.
const PAGE_SIZE = 1000;
const REQUEST_DELAY = 2000;
const MAX_FAILURES = 5;

const COLUMNS = ['time_utc', 'trace_id', 'type', 'success', 'from', 'to', 'amount', 'asset', 'comment'];

export const csvExport = Vue.observable({ address: null, rows: 0, oldest: null, failed: false });

let job = null;

const sleep = ms => new Promise((resolve) => { setTimeout(resolve, ms); });

// Raw integer amount -> exact decimal string, without float rounding:
const toUnits = (raw, decimals) => {
    if (raw === undefined || raw === null || !/^\d+$/.test(String(raw))) return '';
    const places = Number.isInteger(decimals) ? decimals : 9;
    const digits = String(raw).padStart(places + 1, '0');
    const int = digits.slice(0, digits.length - places).replace(/^0+(?=\d)/, '');
    const frac = digits.slice(digits.length - places).replace(/0+$/, '');
    return frac ? `${int}.${frac}` : int;
};

// Comments and token names come from anyone on chain, so a leading = + - @
// must not reach Excel as a formula:
const text = value => (/^[=+\-@\t\r]/.test(value || '') ? `'${value}` : value);

const cell = (value) => {
    const str = value === undefined || value === null ? '' : String(value);
    return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const toLine = (tx, msg, from, to, amount, asset) => [
    new Date(tx.timestamp).toISOString(), text(tx.hash), msg.event, msg.is_success !== false,
    from, to, amount, asset, text(msg.message),
].map(cell).join(',');

const toLines = (tx, msg) => {
    const meta = msg.meta || {};

    // A swap is two rows of the same trace: what went to the DEX and what came back from it.
    if (msg.amount_in !== undefined) {
        const assetIn = text(meta.symbol_in) || meta.jetton_in_address || 'GRAM';
        const assetOut = text(meta.symbol_out) || meta.jetton_out_address || 'GRAM';

        return [
            toLine(tx, msg, msg.from, msg.to, toUnits(meta.amount_in, meta.decimals_in), assetIn),
            toLine(tx, msg, msg.to, msg.from, toUnits(meta.amount_out, meta.decimals_out), assetOut),
        ];
    }

    // Jetton transfers and a few others keep the amount in the action:
    const amount = toUnits(msg.amount ?? msg.action?.amount ?? meta.amount, meta.decimals);
    const asset = text(meta.symbol) || meta.jetton_address || (amount ? 'GRAM' : text(meta.name));

    return [toLine(tx, msg, msg.from, msg.to, amount, asset)];
};

// getToncenterActions reads the filters from the store on every call:
const filtersOf = address => JSON.stringify([
    store.state.datePickerFilter, store.state.excludeGroups, store.state.transactionDirection,
    store.getters.initiatedByAccountFor(address),
]);

const finish = (current) => {
    if (job !== current) return;
    job = null;
    csvExport.address = null;

    if (current.lines.length > 1) {
        const blob = new Blob(['\uFEFF', current.lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${i18n.t('address.export.file_name', {
            address: current.address,
            date: new Date().toISOString().slice(0, 10),
        })}.csv`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 10000);
    }
};

export const stopCsvExport = () => job && finish(job);

// Drops the export without saving a file: the loop sees another job and stops.
export const cancelCsvExport = () => {
    job = null;
    csvExport.address = null;
};

export const startCsvExport = async (address) => {
    if (job) return;

    const current = { address, lines: [COLUMNS.join(',')] };
    const filters = filtersOf(address);
    let nextPage = {};
    let failures = 0;

    job = current;
    Object.assign(csvExport, { address, rows: 0, oldest: null, failed: false });

    while (job === current) {
        let page;

        try {
            page = await getToncenterActions(address, { limit: PAGE_SIZE, ...nextPage }); // eslint-disable-line no-await-in-loop
            failures = 0;
        } catch (error) {
            failures += 1;
            if (failures >= MAX_FAILURES) {
                csvExport.failed = true;
                break;
            }
            await sleep(REQUEST_DELAY * failures); // eslint-disable-line no-await-in-loop
            continue; // eslint-disable-line no-continue
        }

        // Stopped while the request was in flight:
        if (job !== current) break;

        // Later pages would come with other filters, so keep only what matches the first ones:
        if (filtersOf(address) !== filters) {
            csvExport.failed = true;
            break;
        }

        // Pages go by toncenter's cursor; a trace cut by the limit is held back and comes
        // whole with the next page (see getToncenterActions), so a page may even be empty:
        page.forEach(tx => tx.messages.forEach((msg) => {
            if (msg && !msg.is_external) current.lines.push(...toLines(tx, msg));
        }));

        csvExport.rows = current.lines.length - 1;
        if (page.length) csvExport.oldest = page.at(-1).timestamp;

        if (!page.nextPage) break;
        nextPage = page.nextPage;

        await sleep(REQUEST_DELAY); // eslint-disable-line no-await-in-loop
    }

    finish(current);
};
